import Cookies from 'universal-cookie';

import { paramOrNul } from '@common/config/utils';
import { log, LOG_LEVELS } from '@common/config/app_logger';
import { AnalyticsProvider } from './AnalyticsProvider';
import { EventEmitter, type Callback } from './EventEmitter';

const eventNameMap: Record<string, string> = {
  'purchase started': 'InitiateCheckout',
  Purchase: 'Purchase',
  signup: 'CompleteRegistration',
  'favorite advisor': 'AddToCart',
  'advisor profile view': 'ViewContent',
  'spent credits': 'SpentCredits',
  'achievement unlocked': 'AchievementUnlocked',
  'credit card added': 'AddPaymentInfo',
  'apple pay added': 'AddPaymentInfo'
};

const mapSignUpMethodToLoginType = (loginType: string) => {
  const typesList: { [key: string]: string } = {
    'Google Plus': 'google',
    Email: 'email',
    Facebook: 'facebook'
  };
  return typesList[loginType];
};

export class FacebookProvider extends AnalyticsProvider {
  public get name(): string { return 'Facebook'; }

  private isInitialized: boolean = false;

  private propertiesMap: Record<string, any>;

  private emitter: EventEmitter = new EventEmitter();

  private staticProperties: Record<string, any>;

  private mapProperties = (eventName: string, eventProperties?: Record<string, any>): Record<string, any> | undefined => {
    if (!eventProperties || !eventNameMap[eventName]) return eventProperties;

    const mappedName = eventNameMap[eventName];

    if (!this.propertiesMap[mappedName]) return {};

    return Object.keys(this.propertiesMap[mappedName]).reduce((obj: any, key: string) => {
      if (eventProperties[key]) {
        this.propertiesMap[mappedName][key].forEach((x: string) => {
          if (key === 'signup method') {
            obj[x] = mapSignUpMethodToLoginType(eventProperties[key]);
            return;
          }

          obj[x] = eventProperties[key];
        });
      }
      return obj;
    }, {});
  };

  private extraFacebookPixelEventParams = (eventName: string) => {
    const {
      user: {
        user
      }
    } = window.store.getState();

    if (!user) return null;

    const {
      id, email, dateOfBirth, gender, facebookId
    } = user;

    const cookies = new Cookies();

    // https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters
    const extraParams: Record<string, any> = {
      ...paramOrNul('external_id', id),              // id
      ...paramOrNul('em', email),                    // email
      client_user_agent: window.navigator.userAgent, // user_agent
      ...paramOrNul('fbp', cookies.get('_fbp')),     // browser_id
      ...paramOrNul('fbc', cookies.get('_fbc')),     // click_id
      ...paramOrNul('db', dateOfBirth),              // date_of_birth
      ...paramOrNul('ge', gender),                   // gender
      ...paramOrNul('fb_login_id', facebookId)       // facebook_login_id
    };

    const mappedName = eventNameMap[eventName];
    const staticProps = this.staticProperties[mappedName];

    if (!mappedName || !staticProps) return extraParams;

    Object.keys(staticProps).forEach(key => {
      extraParams[key] = staticProps[key];
    });

    return extraParams;
  };

  private initProperties = () => {
    this.propertiesMap = {
      InitiateCheckout: {
        'local currency': [window.FB?.AppEvents?.ParameterNames?.CURRENCY]
      },
      Purchase: {
        currency: [window.FB?.AppEvents?.ParameterNames?.CURRENCY],
        price: ['value', 'fb_value']
      },
      CompleteRegistration: {
        'signup method': [window.FB?.AppEvents?.ParameterNames?.REGISTRATION_METHOD]
      },
      AddToCart: {
        'advisor id': [window.FB?.AppEvents?.ParameterNames?.CONTENT_ID]
      },
      ViewContent: {
        'advisor id': [window.FB?.AppEvents?.ParameterNames?.CONTENT_ID]
      },
      SpentCredits: {
        price: ['value', 'fb_value', window.FB?.AppEvents?.ParameterNames?.NUM_ITEMS]
      }
    };
    const userCurrency = window.store?.getState()?.user?.user?.currency || 'USD';
    this.staticProperties = {
      CompleteRegistration: {
        [window.FB?.AppEvents?.ParameterNames?.CURRENCY]: userCurrency
      },
      AddToCart: {
        [window.FB?.AppEvents?.ParameterNames?.CONTENT_TYPE]: 'product'
      },
      ViewContent: {
        [window.FB?.AppEvents?.ParameterNames?.CONTENT_TYPE]: 'product'
      },
      SpentCredits: {
        [window.FB?.AppEvents?.ParameterNames?.CURRENCY]: userCurrency
      }
    };
  };

  private handleFbLoaded() {
    this.initProperties();
    this.isInitialized = true;
    document.removeEventListener('fbLoaded', this.handleFbLoaded.bind(this));
    this.emitter.emit('initialized');
  }

  public mapAndExtendProperties = (eventName: string, eventProperties?: Record<string, any>) => ({
    ...this.mapProperties(eventName, eventProperties),
    ...this.extraFacebookPixelEventParams(eventName)
  });

  public get initialized() { return this.isInitialized; }

  public init(callback: Callback) {
    const subscription = this.emitter.subscribe('initialized', () => { callback(); subscription.unsubscribe(); });
    document.addEventListener('fbLoaded', this.handleFbLoaded.bind(this));

    if ('fbq' in window && 'FB' in window && typeof window.FB !== 'undefined') {
      this.handleFbLoaded();
    }
  }

  public authorize() { }

  public reset() { }

  public trackEvent(eventName: string, eventProperties?: Record<string, any>, additionalProperties?: Record<string, any>) {
    const name = eventNameMap[eventName];
    if (!name) return;

    log(LOG_LEVELS.INFO, 'Facebook', `track event: ${ eventName }`);
    log(LOG_LEVELS.DEBUG, 'Facebook', 'eventProperties');

    const data = { eventID: additionalProperties.conversionEventId };

    window.fbq(
      additionalProperties.isCustomEvent ? 'trackCustom' : 'track',
      name,
      eventProperties,
      data
    );
  }

  public trackPageVisit() { }
}
